import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PackageSearch, Plus, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { BridgeSourceNote } from "@/components/common/BridgeSourceNote";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SeedBadge } from "@/components/common/SeedBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { FUND_STATUS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Fundstuecke() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", brocki_name: "", condition: "", status: "eingegangen" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/fundstuecke").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/fundstuecke", form);
      toast.success("Fundstück erfasst");
      setOpen(false); setForm({ title: "", category: "", brocki_name: "", condition: "", status: "eingegangen" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const filtered = (items || []).filter((f) => filter === "all" || f.status === filter);

  return (
    <div data-testid="fundstuecke-page">
      <PageHeader title="Fundstücke" subtitle="Eingegangene, moderierte und freigegebene Fundstücke aller Standorte.">
        {can("fundstuecke", "write") && (
          <Button data-testid="fund-create-button" onClick={() => setOpen(true)} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight"><Plus className="h-4 w-4" /> Fundstück erfassen</Button>
        )}
      </PageHeader>

      <BridgeSourceNote />

      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="fund-filter-all">Alle</FilterChip>
        {Object.entries(FUND_STATUS).map(([k, v]) => (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} testid={`fund-filter-${k}`}>{v.label}</FilterChip>
        ))}
      </div>

      <Panel testid="fund-panel" noPad>
        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-4"><EmptyState icon={PackageSearch} title="Keine Fundstücke" description="Für diesen Filter liegen keine Einträge vor." testid="fund-empty" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                <th className="px-5 py-3">Bezeichnung</th><th className="px-5 py-3">Kategorie</th><th className="px-5 py-3">Brocki</th><th className="px-5 py-3">Eingang</th><th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-fundio-border">
              {filtered.map((f) => (
                <tr key={f.id} data-testid={`fund-row-${f.id}`} className="bg-white transition-colors hover:bg-fundio-subtle">
                  <td className="px-5 py-3.5 font-medium text-fundio-ink">{f.title}<SeedBadge show={f.is_seed} /></td>
                  <td className="px-5 py-3.5 text-fundio-muted">{f.category}</td>
                  <td className="px-5 py-3.5 text-fundio-muted">{f.brocki_name || "—"}</td>
                  <td className="px-5 py-3.5 text-fundio-faint">{formatDate(f.found_date)}</td>
                  <td className="px-5 py-3.5"><StatusBadge map={FUND_STATUS} value={f.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="fund-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Fundstück erfassen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5"><Label className="text-xs text-fundio-muted">Bezeichnung</Label><Input data-testid="fund-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Kategorie</Label><Input data-testid="fund-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Brocki</Label><Input value={form.brocki_name} onChange={(e) => setForm({ ...form, brocki_name: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Zustand</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FUND_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-fundio-muted">Abbrechen</Button>
            <Button data-testid="fund-save-button" disabled={saving || !form.title || !form.category} onClick={save} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-fundio-gold bg-status-warning-bg text-status-warning" : "border-fundio-border bg-white text-fundio-muted hover:text-fundio-ink"}`}>{children}</button>
  );
}
